import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LifeBuoy, Plus, Loader2, ArrowRight, ShieldAlert } from "lucide-react";
import { STATUS_META, categoryLabel, formatWhen } from "@/lib/complaints";

const Row = ({ c, perspective }) => {
  const meta = STATUS_META[c.status] || STATUS_META.open;
  const other = perspective === "reporter"
    ? c.respondent_name || c.respondent_email || "Unassigned"
    : c.reporter_name || c.reporter_email || "Anonymous";
  return (
    <Link
      to={`/complaints/${c.complaint_id}`}
      data-testid={`complaint-row-${c.complaint_id}`}
      className="block bg-white border border-black/10 rounded-xl px-5 py-4 hover:border-[#C05A3C]/60 hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3EFE9] flex items-center justify-center shrink-0">
          <ShieldAlert className="w-5 h-5 text-[#C05A3C]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
            <span className="text-xs text-muted-foreground">{categoryLabel(c.category)}</span>
            {c.payout_held && <Badge className="bg-[#1A2024] text-[#D9923B] text-[10px]">PAYOUT HELD</Badge>}
          </div>
          <div className="font-semibold mt-1 truncate">{c.subject}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {perspective === "reporter" ? "Against" : "From"} {other} · {formatWhen(c.updated_at)}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground mt-2" />
      </div>
    </Link>
  );
};

const Empty = ({ children }) => (
  <Card className="p-10 text-center border-dashed border-black/10 bg-[#FBF9F4]">
    <LifeBuoy className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
    <p className="text-sm text-muted-foreground">{children}</p>
  </Card>
);

const Complaints = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filed, setFiled] = useState([]);
  const [against, setAgainst] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      api.get("/complaints?role=reporter"),
      api.get("/complaints?role=respondent"),
    ])
      .then(([a, b]) => {
        setFiled(a.data.complaints || []);
        setAgainst(b.data.complaints || []);
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) return null;

  return (
    <div className="container-narrow py-10 max-w-5xl">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Resolution centre</div>
          <h1 className="font-display text-4xl">Your cases</h1>
          <p className="text-muted-foreground font-sans-ui mt-2 max-w-2xl">
            Open a dispute for orders, listings, payments or user behaviour. We&apos;ll keep
            the seller&apos;s payout on hold while we investigate.
          </p>
        </div>
        <Button
          data-testid="new-complaint-btn"
          onClick={() => navigate("/complaints/new")}
          className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md"
        >
          <Plus className="w-4 h-4 mr-1" /> File a complaint
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <Tabs defaultValue="filed" data-testid="complaints-tabs">
          <TabsList className="bg-[#F3EFE9]">
            <TabsTrigger value="filed" data-testid="tab-filed">Filed by me ({filed.length})</TabsTrigger>
            <TabsTrigger value="against" data-testid="tab-against">Against me ({against.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="filed" className="space-y-3 mt-6">
            {filed.length === 0 ? (
              <Empty>You haven&apos;t opened any cases. <Link to="/complaints/new" className="text-[#C05A3C] underline">File one</Link> if something&apos;s wrong.</Empty>
            ) : (
              filed.map((c) => <Row key={c.complaint_id} c={c} perspective="reporter" />)
            )}
          </TabsContent>
          <TabsContent value="against" className="space-y-3 mt-6">
            {against.length === 0 ? (
              <Empty>No complaints filed against you. Keep up the good work.</Empty>
            ) : (
              against.map((c) => <Row key={c.complaint_id} c={c} perspective="respondent" />)
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Complaints;
